// src/api/client.ts
import axios, { AxiosInstance, AxiosError } from 'axios';

export const API_URL = import.meta.env.VITE_API_URL

// Create an interface for token management
interface TokenManager {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
  onTokenRefreshFailed: () => void;
}

// Create a reusable API client factory
export const createApiClient = (tokenManager: TokenManager): AxiosInstance => {
  // Create axios instance
  const client = axios.create({
    baseURL: API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add interceptor for adding auth token to requests
  client.interceptors.request.use(
    (config) => {
      const token = tokenManager.getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Add interceptor for handling token refresh
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (!error.config) {
        return Promise.reject(error);
      }

      const originalRequest = error.config;

      // If error is 401 and we haven't tried refreshing the token yet
      if (error.response?.status === 401 && !(originalRequest as any)._retry) {
        (originalRequest as any)._retry = true;

        try {
          const refreshToken = tokenManager.getRefreshToken();
          if (!refreshToken) {
            // No refresh token available, clear tokens
            tokenManager.clearTokens();
            tokenManager.onTokenRefreshFailed();
            return Promise.reject(error);
          }

          // Try to refresh the token
          const response = await axios.post<{ access_token: string, refresh_token: string }>(
            `${API_URL}/auth/refresh`,
            {
              token: refreshToken
            }
          );

          const { access_token, refresh_token } = response.data;

          // Update tokens
          tokenManager.setTokens(access_token, refresh_token);

          // Retry the original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
          }
          return client(originalRequest);
        } catch (refreshError) {
          // If refresh failed, clear tokens
          tokenManager.clearTokens();
          tokenManager.onTokenRefreshFailed();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};